'use client';

import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { ImageIcon, Plus, X, RotateCw, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { usePageBuilderContext } from '../page-builder-context';
import { uploadScreenshot } from '@/lib/services/uploadService';
import type { AsyncUpload } from '@/lib/hooks/use-async-uploads';
import type { PreviewImageDto } from '@/lib/services/productService';
import { MaterialSpinner } from '@/components/ui/material-spinner';
import { cn } from '@/lib/utils';

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_IMAGES = 5;
const ASPECT_RATIO = 16 / 9;
const UPLOAD_KEY_PREFIX = 'preview-images:';

// ============================================================================
// CROPPING HELPER
// ============================================================================

async function getCroppedImage(
  imageSrc: string,
  croppedAreaPixels: Area
): Promise<File> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      const file = new File([blob], `preview-${Date.now()}.webp`, { type: 'image/webp' });
      resolve(file);
    }, 'image/webp', 0.99);
  });
}

// ============================================================================
// SORTABLE ID
// ============================================================================

/**
 * Stable identity for a preview image within the sortable list. The upload
 * `imageKey` is present for every preview image (create and edit round-trips),
 * with the display URL as a defensive fallback — never the array index, which
 * changes as items are reordered.
 */
function imageId(image: PreviewImageDto): string {
  return image.imageKey || image.imageUrl;
}

// ============================================================================
// SORTABLE IMAGE SLOT (a filled slot that can be dragged to reorder)
// ============================================================================

interface SortableImageSlotProps {
  image: PreviewImageDto;
  index: number;
  /** Reordering is only offered when there is more than one image. */
  sortable: boolean;
  onRemove: () => void;
}

function SortableImageSlot({ image, index, sortable, onRemove }: SortableImageSlotProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: imageId(image), disabled: !sortable });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged item above its siblings so it reads as picked-up.
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(sortable ? listeners : {})}
      className={cn(
        'relative group aspect-video rounded-lg overflow-hidden bg-muted',
        sortable && 'cursor-grab active:cursor-grabbing touch-none',
        isDragging && 'ring-2 ring-primary'
      )}
    >
      <img
        src={image.imageUrl}
        alt={`Preview image ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none select-none"
        draggable={false}
      />

      {/* Drag affordance — the whole card drags; this hints it's reorderable */}
      {sortable && (
        <div
          className={cn(
            'absolute top-2 left-2 w-6 h-6 rounded-full',
            'bg-black/50 text-white flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none'
          )}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}

      <button
        type="button"
        onClick={onRemove}
        // Never let a press on the remove control start a drag.
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          'absolute top-2 right-2 w-6 h-6',
          'bg-black/50 hover:bg-destructive text-white rounded-full',
          'flex items-center justify-center',
          'opacity-0 group-hover:opacity-100 transition-opacity'
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// UPLOAD SLOT (empty dropzone that opens the file picker / accepts a drop)
// ============================================================================

interface UploadSlotProps {
  index: number;
  onUpload: (file: File) => void;
}

function UploadSlot({ index, onUpload }: UploadSlotProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) {
      onUpload(file);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => document.getElementById(`preview-input-${index}`)?.click()}
      className={cn(
        'aspect-video rounded-lg border-2 border-dashed',
        'flex items-center justify-center cursor-pointer',
        'transition-all duration-200',
        isDragging ? 'border-foreground/30 bg-muted/50 scale-[1.02]' : 'border-border'
      )}
    >
      <input
        type="file"
        id={`preview-input-${index}`}
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
        className="sr-only"
      />

      <Plus className="h-6 w-6 text-foreground-tertiary" />
    </div>
  );
}

// ============================================================================
// PENDING SLOT (optimistic preview while the upload runs in the background)
// ============================================================================

interface PendingSlotProps {
  upload: AsyncUpload;
  onRetry: () => void;
  onDismiss: () => void;
}

function PendingSlot({ upload, onRetry, onDismiss }: PendingSlotProps) {
  return (
    <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
      <img
        src={upload.previewUrl}
        alt="Загружаемое изображение"
        className="w-full h-full object-cover"
      />

      {upload.status === 'uploading' && (
        <div className="absolute top-2 right-2 rounded-full bg-card/95 p-1.5">
          <MaterialSpinner className="h-4 w-4" />
        </div>
      )}

      {upload.status === 'error' && (
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 p-2">
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary-hover text-primary-foreground text-xs font-medium rounded-lg"
          >
            <RotateCw className="h-3 w-3" />
            Повторить
          </button>
          <span className="text-[11px] text-white/70 text-center">
            Не удалось загрузить
          </span>
          <button
            type="button"
            onClick={onDismiss}
            className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-destructive text-white rounded-full flex items-center justify-center"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PreviewImagesUploader() {
  const { formData, updateMetadataWith, uploads, startUpload, retryUpload, dismissUpload } =
    usePageBuilderContext();

  // Cropper state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const images = formData.metadata.previewImages || [];

  // In-flight uploads for this section (each gets its own slot)
  const pendingUploads = Object.entries(uploads).filter(([key]) =>
    key.startsWith(UPLOAD_KEY_PREFIX)
  );

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const openCropper = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageToCrop(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropModalOpen(true);
  };

  const handleCropSave = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    try {
      const croppedFile = await getCroppedImage(imageToCrop, croppedAreaPixels);
      setCropModalOpen(false);
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);

      // Fire-and-track: the cropped preview appears instantly in its own
      // slot while the upload runs in the background
      startUpload(
        `${UPLOAD_KEY_PREFIX}${crypto.randomUUID()}`,
        croppedFile,
        uploadScreenshot,
        (data) => ({
          warmUrl: data.originalUrl,
          commit: () => {
            const previewImage: PreviewImageDto = {
              imageKey: data.key,
              imageUrl: data.originalUrl,
              thumbnailUrl: data.thumbnailUrl,
            };
            updateMetadataWith('previewImages', (prev) => [...(prev || []), previewImage]);
          },
        })
      );
    } catch (e) {
      console.error('Error:', e);
      toast.error(e instanceof Error ? e.message : 'Ошибка обработки');
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
  };

  const handleRemove = (index: number) => {
    updateMetadataWith('previewImages', (prev) =>
      (prev || []).filter((_, i) => i !== index)
    );
  };

  // Reorder is a pure client-side array move — no re-upload, no reload.
  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    updateMetadataWith('previewImages', (prev) => {
      const list = prev || [];
      const from = list.findIndex((img) => imageId(img) === active.id);
      const to = list.findIndex((img) => imageId(img) === over.id);
      if (from === -1 || to === -1) return list;
      return arrayMove(list, from, to);
    });
  };

  // A small distance / hold threshold keeps taps on the remove button and the
  // upload slot working — a drag only starts on deliberate movement.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } })
  );

  const totalImages = images.length + pendingUploads.length;
  const canAdd = totalImages < MAX_IMAGES;
  const canReorder = images.length > 1;

  return (
    <>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-foreground-tertiary" />
          <span className="text-sm font-medium text-foreground">
            Превью изображения
          </span>
          <span className="text-xs text-foreground-tertiary">(необязательно)</span>
        </div>

        {/* Description */}
        <p className="text-xs text-foreground-secondary">
          Эти изображения будут использоваться в карточках продукта и в секции «Избранное»
          на главной странице. Требуется соотношение 16:9.
        </p>

        {/* Image grid — filled slots drag to reorder, no re-upload needed */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleReorder}
        >
          <div className="grid grid-cols-2 gap-3">
            <SortableContext
              items={images.map(imageId)}
              strategy={rectSortingStrategy}
            >
              {images.map((image, index) => (
                <SortableImageSlot
                  key={imageId(image)}
                  image={image}
                  index={index}
                  sortable={canReorder}
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </SortableContext>
            {pendingUploads.map(([key, upload]) => (
              <PendingSlot
                key={key}
                upload={upload}
                onRetry={() => retryUpload(key)}
                onDismiss={() => dismissUpload(key)}
              />
            ))}
            {canAdd && (
              <UploadSlot index={totalImages} onUpload={openCropper} />
            )}
          </div>
        </DndContext>

        {/* Help text */}
        <p className="text-xs text-foreground-tertiary text-center">
          {canReorder
            ? `Перетащите, чтобы изменить порядок · Максимум ${MAX_IMAGES} изображений`
            : `Максимум ${MAX_IMAGES} изображений`}
        </p>
      </div>

      {/* Crop Modal */}
      {cropModalOpen && imageToCrop && (
        <div className="fixed inset-0 z-50 bg-black">
          <div className="absolute inset-0 bottom-16">
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT_RATIO}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={true}
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-black/90 backdrop-blur border-t border-white/10 px-4 py-3">
            <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs text-white/60">−</span>
                <input
                  type="range"
                  min={0.5}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
                <span className="text-xs text-white/60">+</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleCropSave}
                  className="px-4 py-1.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary-hover rounded-lg"
                >
                  Готово
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
